import { IImporterReject } from 'library/ts/interfaces/importer/IImporterReject';
import { IImporterResolve } from 'library/ts/interfaces/importer/IImporterResolve';
import { IImportVO } from 'library/ts/interfaces/importer/IImportVO';
import { ImportType } from 'library/ts/model/enums/config/ImportType';
import { IImporter } from 'library/ts/utilities/interfaces/IImporter';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Importer class for loading JavaScript dependencies
 */
export class Importer implements IImporter {
	#logger: ILogger;

	constructor() {
		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', this.name + ':' ] } );
	}

	get name(): string {
		return 'Importer';
	}

	/**
	 * @description Imports dependencies
	 * @param {IImportVO[]} imports
	 * @param {Boolean} sequence
	 * @param {Number} stepDelay
	 * @return {Promise<IImporterResolve | IImporterReject>}
	 */
	import = ( imports: IImportVO[] = [], sequence: boolean = false, stepDelay: number = undefined ): Promise<IImporterResolve | IImporterReject> => {
		const utils = window?.PARA?.PlayerServices?.Utilities;

		/**
		 * @description Number of scripts to load
		 * @type {number}
		 */
		let numScripts: number = 0;

		/**
		 * @description Number of loaded scripts
		 * @type {number}
		 */
		let scriptsLoaded: number = 0;

		/**
		 * @description Checks presence of import namespace in DOM
		 * @param {IImportVO} importVO
		 * @return {boolean}
		 */
		const isPresent = ( importVO: IImportVO ) => {
			return utils.Import.alreadyPresent( importVO.url, importVO.namespace );
		};

		// Return it as a Promise
		return new Promise(
			( resolve, reject ) => {

				/**
				 * @description Sequential load method
				 * @param {number} scriptsLoaded
				 */
				const loadSequential = ( scriptsLoaded: number ): void => {
					const importVO: IImportVO = imports[ scriptsLoaded ];

					const isComplete = () => {
						if ( scriptsLoaded < numScripts ) {
							this.#logger.log( scriptsLoaded, numScripts, 'Not complete yet...' );
							setTimeout(
								() => {
									importVO.exists = isPresent( importVO );
									importVO.failed = !isPresent( importVO );
									loadSequential( scriptsLoaded );
								},
								stepDelay
							);
						} else {
							this.#logger.log( scriptsLoaded, numScripts, 'all done...' );

							const loaded: IImportVO[] = [];
							const failed: IImportVO[] = [];

							for ( let i = 0; i < imports.length; i++ ) {
								// IMPORTANT: update script failed flag per it's exists value
								imports[ i ].failed = !imports[ i ].exists;

								if ( imports[ i ].exists === true ) loaded.push( imports[ i ] );
								if ( imports[ i ].failed === true || imports[ i ].exists === false ) failed.push( imports[ i ] );
							}

							// no failed imports
							switch ( failed.length === 0 ) {
								case true:
									resolve(
										{
											message: 'success',
											imports: loaded
										} as IImporterResolve
									);
									break;
								case false:
									reject(
										{
											message: 'failure',
											imports: failed
										} as IImporterReject
									);
									break;
							}
						}
					};

					// check if import os already present
					switch ( isPresent( importVO ) ) {

						case true: // import is present
							importVO.exists = true;
							importVO.failed = false;
							scriptsLoaded++;
							isComplete();
							break;

						case false: // import is not found
							// select import method type for script or style
							const method = ( importVO.type === ImportType.SCRIPT ? utils.Import.loader.js : utils.Import.loader.css );

							Promise.all(
								[ method( importVO, false, utils ) ]
							).then(
								( data: IImportVO[] ) => {
									this.#logger.log( 'Resolved!', data[ 0 ] );
									data[ 0 ].exists = isPresent( data[ 0 ] );
									data[ 0 ].failed = false;
									scriptsLoaded++;
									isComplete();
								}
							).catch(
								( data: IImportVO[] ) => {
									this.#logger.warn( 'Rejected!', data[ 0 ] );
									data[ 0 ].failed = true;
									scriptsLoaded++;
									isComplete();
								}
							);
							break;

					}

				};

				/**
				 * @description Regular load type
				 */
				const load = (): void => {
					// Placeholder for Loader tasks
					const tasks: any[] = [];

					// add loader tasks
					for ( let i = 0; i < imports.length; i++ ) {
						// select import method type for script or style
						const method = ( imports[ i ].type === ImportType.SCRIPT ? utils.Import.loader.js : utils.Import.loader.css );

						tasks.push(
							method( imports[ i ], false, utils )
						);
					}

					Promise.all(
						tasks
					).then(
						( messages ) => {
							this.#logger.log( 'Resolved!', messages );
						}
					).catch(
						( error ) => {
							this.#logger.warn( 'Rejected!', error );
						}
					);
				};

				// INFO: START

				switch ( imports.length > 0 ) {

					case true: //  we have imports
						numScripts = imports.length;
						scriptsLoaded = 0;

						switch ( sequence ) {
							case true: // sequential loading
								loadSequential( scriptsLoaded );
								break;
							case false: // loop sequential loading
								load();
								break;
						}

						break;

					case false: // no imports found
						// resolve promise...
						resolve(
							{ message: 'Imports loaded', imports: imports } as IImporterResolve
						);
						break;

				}

			}
		);

	};

}